import { format, subDays, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, eachMonthOfInterval } from 'date-fns';
import { DeploymentRequest } from '../types';
import { assignCategoricalColors } from './chartColors';

/**
 * Convert UTC date to IST and format as DD/MM/YYYY HH:MM:SS
 */
export const formatToIST = (dateString: string | Date, includeTime: boolean = true): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  
  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(includeTime && {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
  };
  
  const formatted = date.toLocaleString('en-IN', options);
  
  // Convert from "DD/MM/YYYY, HH:MM:SS" to "DD/MM/YYYY HH:MM:SS"
  return formatted.replace(',', '');
};

/**
 * Format date for display in UI (DD/MM/YYYY)
 */
export const formatDateOnly = (dateString: string | Date): string => {
  return formatToIST(dateString, false);
};

/**
 * Format date for display with time (DD/MM/YYYY HH:MM)
 */
export const formatDateTime = (dateString: string | Date): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).replace(',', '');
};

export const generateMonthlyData = (deployments: DeploymentRequest[]) => {
  const months = eachMonthOfInterval({
    start: subMonths(new Date(), 5),
    end: new Date()
  });

  return months.map(month => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    
    const monthDeployments = deployments.filter(d => {
      if (!d.requestedAt) return false;
      const deployDate = new Date(d.requestedAt);
      return deployDate >= monthStart && deployDate <= monthEnd;
    });

    const successful = monthDeployments.filter(d => d.status === 'deployed').length;
    const failed = monthDeployments.filter(d => d.status === 'rejected').length;

    return {
      month: format(month, 'MMM yyyy'),
      deployments: monthDeployments.length,
      successful,
      failed
    };
  });
};

export const generateDailyData = (deployments: DeploymentRequest[]) => {
  const days = eachDayOfInterval({
    start: subDays(new Date(), 29),
    end: new Date()
  });

  return days.map(day => {
    const dayStart = new Date(day.setHours(0, 0, 0, 0));
    const dayEnd = new Date(day.setHours(23, 59, 59, 999));
    
    const dayDeployments = deployments.filter(d => {
      if (!d.requestedAt) return false;
      const deployDate = new Date(d.requestedAt);
      return deployDate >= dayStart && deployDate <= dayEnd;
    });

    return {
      date: format(day, 'MMM dd'),
      deployments: dayDeployments.length,
      pending: dayDeployments.filter(d => d.status === 'pending').length,
      approved: dayDeployments.filter(d => d.status === 'tl_approved' || d.status === 'pm_approved').length,
      deployed: dayDeployments.filter(d => d.status === 'deployed').length
    };
  });
};

export const generateModuleData = (deployments: DeploymentRequest[]) => {
  const currentMonth = new Date();
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  // Helper to map module IDs/objects to names
  const mapModuleNames = (modules: any) => {
    if (!modules) return [];
    const names: string[] = [];
    if (Array.isArray(modules)) {
      for (const mod of modules) {
        if (mod && typeof mod === 'object') {
          if (mod.name) names.push(mod.name);
          else if (mod._id) names.push(mod._id);
        } else if (typeof mod === 'string') {
          names.push(mod);
        }
      }
    } else if (typeof modules === 'object' && modules !== null) {
      if (modules.name) names.push(modules.name);
      else if (modules._id) names.push(modules._id);
    } else if (typeof modules === 'string') {
      names.push(modules);
    }
    return names;
  };

  const monthDeployments = deployments.filter(d => {
    if (!d.requestedAt) return false;
    const deployDate = new Date(d.requestedAt);
    return deployDate >= monthStart && deployDate <= monthEnd;
  });

  const moduleCount: { [key: string]: number } = {};
  monthDeployments.forEach(d => {
    // Support both d.module and d.modules
    const names = [
      ...mapModuleNames(d.modules),
      ...mapModuleNames(d.module)
    ].filter(n => n && n !== 'undefined' && n !== '');
    if (names.length === 0) {
      moduleCount['Unknown'] = (moduleCount['Unknown'] || 0) + 1;
    } else {
      names.forEach(name => {
        moduleCount[name] = (moduleCount[name] || 0) + 1;
      });
    }
  });

  // Deduplicate and filter out empty/undefined, then assign fixed-order
  // categorical colors (never cycled — see utils/chartColors.ts)
  const entries = Object.entries(moduleCount)
    .filter(([name]) => name && name !== 'undefined' && name !== '')
    .map(([name, value]) => ({ name, value }));

  return assignCategoricalColors(entries);
};